/**
 * Hold categories — the ONE place the Studio editors name them, label them and
 * let an admin link an object to them (JAI-1767).
 *
 * Data: unbound — this module renders and returns values only. The write goes
 * through each editor's EXISTING update op (`updateService`, the event update,
 * the queue update); `resolvesHolds` rides those bodies as a contract field
 * since kit 25cc5fda. No new endpoint, and nothing here calls one.
 *
 * The vocabulary is NOT re-declared: it is `PORTAL_RESOLVABLE_HOLD_CATEGORIES`
 * from the contract, so a sixth category added there appears in every editor
 * without a second edit. Labels come from the catalog under
 * `holds.category.<category>` — the vocabulary NAME of each category
 * ("Financial"), not a hold's resolved copy title ("Outstanding balance"),
 * which is what the identity panel renders.
 */
import type { CSSProperties, ReactNode } from "react";
import { PORTAL_RESOLVABLE_HOLD_CATEGORIES } from "@v0y4ge/api-contracts";
import type { PortalResolvableHoldCategory } from "@v0y4ge/api-contracts";
import { T } from "@v0y4ge/jrni-ui";

export type HoldCategory = PortalResolvableHoldCategory;
export const HOLD_CATEGORIES: readonly HoldCategory[] = PORTAL_RESOLVABLE_HOLD_CATEGORIES;

type Translate = (key: string, fallback?: string) => string;

/** Byte-exact English, so a call site self-heals when the catalog key lands. */
const CATEGORY_EN: Record<string, string> = {
  financial: "Financial",
  academic: "Academic",
  administrative: "Administrative",
  disciplinary: "Disciplinary",
  health: "Health",
};

export function holdCategoryLabel(t: Translate, category: string): string {
  return t(`holds.category.${category}`, CATEGORY_EN[category] ?? category);
}

/** The subject the help sentence talks about. One key per kind rather than an
 *  ICU parameter: an ICU call site renders its raw key in any locale whose
 *  catalog has not shipped the message yet, and these three plain keys carry a
 *  byte-exact English fallback instead. */
export type HoldSubject = "service" | "event" | "queue";
const HELP_EN: Record<HoldSubject, string> = {
  service: "Students with one of these holds are offered this service while the hold is active.",
  event: "Students with one of these holds are offered this event while the hold is active.",
  queue: "Students with one of these holds are offered this queue while the hold is active.",
};
export function holdsResolvesHelp(t: Translate, subject: HoldSubject): string {
  return t(`holds.resolves.help.${subject}`, HELP_EN[subject]);
}
export function holdsResolvesLabel(t: Translate): string {
  return t("holds.resolves.label", "Resolves holds");
}

/** A stored value read back as a clean, de-duplicated category list. Anything
 *  the contract does not know is dropped rather than rendered as a mystery
 *  chip. */
export function readResolvesHolds(value: unknown): HoldCategory[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const out: HoldCategory[] = [];
  for (const raw of value) {
    const v = String(raw);
    if (seen.has(v)) continue;
    if (!(HOLD_CATEGORIES as readonly string[]).includes(v)) continue;
    seen.add(v);
    out.push(v as HoldCategory);
  }
  return out;
}

/**
 * The multi-select. Emits a NEW array in vocabulary order every time, so the
 * write is order-stable and can never carry a duplicate or a sixth entry.
 */
export function ResolvesHoldsPicker({ value, onChange, t, subject, disabled, style }: {
  value: unknown;
  onChange: (next: HoldCategory[]) => void;
  t: Translate;
  subject: HoldSubject;
  disabled?: boolean;
  style?: CSSProperties;
}) {
  const selected = new Set(readResolvesHolds(value));
  const toggle = (cat: HoldCategory) => {
    const next = HOLD_CATEGORIES.filter((c) => (c === cat ? !selected.has(c) : selected.has(c)));
    onChange([...next]);
  };
  return (
    <div style={style}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {HOLD_CATEGORIES.map((cat) => {
          const on = selected.has(cat);
          return (
            /* ui-local: selection only — the value is written by the editor's own save */
            <button
              key={cat}
              type="button"
              role="checkbox"
              aria-checked={on}
              disabled={disabled}
              onClick={() => toggle(cat)}
              data-testid="holds-resolves-option"
              data-id={cat}
              style={{
                display: "inline-flex", alignItems: "center", gap: 7, minHeight: 34,
                padding: "6px 12px", borderRadius: 999, fontFamily: T.font, fontSize: 12.5,
                fontWeight: 600, cursor: disabled ? "default" : "pointer",
                border: `1px solid ${on ? T.p1 : T.n4}`,
                background: on ? T.p4 : T.white,
                color: on ? T.p1 : T.n2,
                opacity: disabled ? 0.55 : 1,
              }}
            >
              <span aria-hidden style={{
                width: 14, height: 14, borderRadius: 4, flex: "none",
                border: `1px solid ${on ? T.p1 : T.n4}`, background: on ? T.p1 : T.white,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                color: T.white, fontSize: 10, lineHeight: 1,
              }}>{on ? "✓" : ""}</span>
              {holdCategoryLabel(t, cat)}
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 11.5, color: T.soft, marginTop: 7, lineHeight: 1.5 }}>
        {holdsResolvesHelp(t, subject)}
      </div>
    </div>
  );
}

/** Read view — chips when the list is non-empty, nothing at all when it is. */
export function ResolvesHoldsChips({ value, t, style, prefix }: {
  value: unknown; t: Translate; style?: CSSProperties; prefix?: ReactNode;
}) {
  const cats = readResolvesHolds(value);
  if (!cats.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, ...style }} data-testid="holds-resolves-chips">
      {prefix}
      {cats.map((cat) => (
        <span key={cat} data-id={cat} style={{
          display: "inline-flex", alignItems: "center", padding: "3px 9px", borderRadius: 999,
          border: `1px solid ${T.n4}`, background: T.n6, color: T.n2,
          fontFamily: T.font, fontSize: 11.5, fontWeight: 600,
        }}>{holdCategoryLabel(t, cat)}</span>
      ))}
    </div>
  );
}
